// Pure presentation builders for the stem-selection ViewModel: the display name, the
// selectable dropdown lists (system profiles, offsets, sizes, size items) and the
// selected-component info. No API access (see stemServices.ts) and no Vue reactivity.
import type { CatalogHead } from '@/domain/catalog/head';
import {
    type CatalogStem,
    formatStemSystem,
    type StemOffset,
    type StemSize,
    stemSizeFromString,
    type StemSystem,
} from '@/domain/catalog/stem';
import { getCatalogComponent } from '@/domain/catalog/catalog';
import type { Url } from '@/domain/types';
import { formatNumberSign } from '@/util/format/formatNumberSign';
import { compact } from 'lodash';
import type {
    SelectableStem,
    SelectableStemOffset,
    SelectableStemSize,
    SelectableSystemProfile,
    SelectedComponentInfo,
    SizeItem,
    StemSystemToggle,
} from '../stem.types';

export type StemNameParts = {
    /** Emphasised size label, e.g. "Size 10". */
    sizeLabel: string;
    /** Remaining descriptor, e.g. "Taperloc Full Profile +3 Head Standard Offset". */
    descriptor: string;
};

// Structured stem name so the view can emphasise the size without v-html.
export const formatStemName = (stem: SelectableStem): StemNameParts => {
    const system = formatStemSystem(stem.catalogStem.system);
    const profile = stem.catalogStem.profile;
    const headOffset = `${formatNumberSign(stem.catalogHead.offset)} Head`;
    const stemOffset = stem.catalogStem.offset;
    return {
        sizeLabel: `Size ${stem.catalogStem.size}`,
        descriptor: `${system} ${profile} ${headOffset} ${stemOffset}`,
    };
};

// Restrict the construct list to the system toggle (preferred / alternative / all).
export const filterStemsBySystem = (
    allConstructs: SelectableStem[],
    selectedStemSystem: StemSystemToggle,
    preferredSystem: StemSystem | null,
): SelectableStem[] => {
    if (!preferredSystem || selectedStemSystem === null) {
        return allConstructs;
    }
    if (selectedStemSystem === 'preferred') {
        return allConstructs.filter((stem) => stem.catalogStem.system === preferredSystem);
    }
    return allConstructs.filter((stem) => stem.catalogStem.system !== preferredSystem);
};

export const hasAlternativeSystem = (
    allConstructs: SelectableStem[],
    preferredSystem: StemSystem | null,
): boolean =>
    preferredSystem !== null &&
    allConstructs.some((stem) => stem.catalogStem.system !== preferredSystem);

export const buildSelectedComponentInfo = (
    catalogStems: Map<string, CatalogStem>,
    catalogHeads: Map<string, CatalogHead>,
    template: { stemUrl: Url; headUrl: Url },
): SelectedComponentInfo => {
    const stem = getCatalogComponent(catalogStems, template.stemUrl);
    const head = getCatalogComponent(catalogHeads, template.headUrl);
    return {
        stemSystem: stem.system,
        stemProfile: stem.profile,
        stemOffset: stem.offset,
        stemSize: stem.size,
        headOffset: head.offset,
    };
};

// Unique system/profile combinations, sorted by display name.
export const buildSelectableSystemProfiles = (
    filteredStems: SelectableStem[],
): SelectableSystemProfile[] => {
    const uniqueCombinations = new Map<string, SelectableSystemProfile>();
    filteredStems.forEach((stem) => {
        const key = `${stem.catalogStem.system}-${stem.catalogStem.profile}`;
        if (!uniqueCombinations.has(key)) {
            uniqueCombinations.set(key, {
                name: `${formatStemSystem(stem.catalogStem.system)} - ${stem.catalogStem.profile}`,
                system: stem.catalogStem.system,
                profile: stem.catalogStem.profile,
            });
        }
    });
    return Array.from(uniqueCombinations.values()).sort((a, b) => a.name.localeCompare(b.name));
};

// Unique offsets available for the manually selected system profile.
export const buildSelectableStemOffsets = (
    filteredStems: SelectableStem[],
    selectedProfile: SelectableSystemProfile | null,
): SelectableStemOffset[] => {
    if (!selectedProfile) {
        return [];
    }
    const uniqueOffsets = new Map<StemOffset, SelectableStemOffset>();
    filteredStems
        .filter(
            (stem) =>
                stem.catalogStem.system === selectedProfile.system &&
                stem.catalogStem.profile === selectedProfile.profile,
        )
        .forEach((stem) => {
            if (!uniqueOffsets.has(stem.catalogStem.offset)) {
                uniqueOffsets.set(stem.catalogStem.offset, {
                    stemUrl: stem.stemUrl,
                    offset: stem.catalogStem.offset,
                });
            }
        });
    return Array.from(uniqueOffsets.values()).sort((a, b) => a.offset.localeCompare(b.offset));
};

// Unique sizes for the selected profile + offset, suitable sizes first.
export const buildSelectableSizes = (
    allConstructs: SelectableStem[],
    selectedProfile: SelectableSystemProfile | null,
    selectedOffset: StemOffset | null,
): SelectableStemSize[] => {
    if (selectedProfile === null || selectedOffset === null) {
        return [];
    }
    const sizeMap = new Map<StemSize, SelectableStemSize>();
    allConstructs
        .filter(
            (stem) =>
                stem.catalogStem.system === selectedProfile.system &&
                stem.catalogStem.profile === selectedProfile.profile &&
                stem.catalogStem.offset === selectedOffset,
        )
        .forEach((stem) => {
            const size = stemSizeFromString(stem.catalogStem.size);
            const existing = sizeMap.get(size);
            // Prefer a suitable stem as the representative for a given size.
            if (!existing || (stem.suitable && !existing.suitable)) {
                sizeMap.set(size, { stemUrl: stem.stemUrl, size, suitable: stem.suitable });
            }
        });
    return Array.from(sizeMap.values()).sort((a, b) => {
        if (a.suitable !== b.suitable) {
            return a.suitable ? -1 : 1;
        }
        return a.size < b.size ? -1 : 1;
    });
};

// The size dropdown's items: suitable sizes under a header, then unsuitable under another.
export const buildSizeItems = (selectableSizes: SelectableStemSize[]): SizeItem[] => {
    const suitable = selectableSizes.filter((stem) => stem.suitable);
    const unsuitable = selectableSizes.filter((stem) => !stem.suitable);
    const divider = { divider: true } as const;
    return compact<SizeItem>([
        suitable.length > 0 ? { header: 'Suggested Sizes' } : null,
        ...suitable,
        suitable.length > 0 && unsuitable.length > 0 ? divider : null,
        unsuitable.length > 0 ? { header: 'Other Sizes' } : null,
        ...unsuitable,
    ]);
};